import type { Cadence } from './reportEnums.constants';
import {
  formatWarehouseYearMoFullMonthLabel,
  parseMeasurementPeriodEndYearMo,
} from './reportMeasurementPeriod.util';

const MONTH_ABBREV_TO_NUM: Record<string, string> = {
  Jan: '01',
  Feb: '02',
  Mar: '03',
  Apr: '04',
  May: '05',
  Jun: '06',
  Jul: '07',
  Aug: '08',
  Sep: '09',
  Oct: '10',
  Nov: '11',
  Dec: '12',
};

/**
 * Parses a short month label such as `Aug 2024` into `YYYYMM`.
 * @param label - Month label from a composed reporting period.
 * @returns Six-digit year-month string or null when not parseable.
 */
export const parseReportMonthLabelToYearMo = (label: string): string | null => {
  const trimmed = label.trim();
  const match = /^([A-Za-z]{3})\s+(\d{4})$/.exec(trimmed);
  if (!match) {
    return null;
  }
  const monthNum = MONTH_ABBREV_TO_NUM[match[1]];
  if (!monthNum) {
    return null;
  }
  return `${match[2]}${monthNum}`;
};

const FULL_MONTH_NAME_TO_NUM: Record<string, string> = {
  january: '01',
  february: '02',
  march: '03',
  april: '04',
  may: '05',
  june: '06',
  july: '07',
  august: '08',
  september: '09',
  october: '10',
  november: '11',
  december: '12',
};

/**
 * Parses a full month label such as `April 2026` into `YYYYMM`.
 * @param label - Chart or copy month label.
 * @returns Six-digit year-month string or null when not parseable.
 */
const parseFullMonthYearLabelToYearMo = (label: string): string | null => {
  const match = /^([A-Za-z]+)\s+(\d{4})$/.exec(label.trim());
  if (!match) {
    return null;
  }
  const monthNum = FULL_MONTH_NAME_TO_NUM[match[1].toLowerCase()];
  if (!monthNum) {
    return null;
  }
  return `${match[2]}${monthNum}`;
};

/**
 * Formats a cumulative “through” month for the Member Engagement funnel heading.
 * Prefers a chart month (`Apr 2026` / `April 2026`); warehouse ranges use the end month.
 * @param monthOrPeriod - Last trend month or measurement period label.
 * @returns Label such as `Through April 2026`, or empty when the input is blank.
 */
export const formatEngagementFunnelThroughLabel = (
  monthOrPeriod: string,
): string => {
  const trimmed = monthOrPeriod.trim();
  if (!trimmed) {
    return trimmed;
  }
  const yearMo =
    parseMeasurementPeriodEndYearMo(trimmed) ??
    parseReportMonthLabelToYearMo(trimmed) ??
    parseFullMonthYearLabelToYearMo(trimmed);
  if (yearMo) {
    return `Through ${formatWarehouseYearMoFullMonthLabel(yearMo)}`;
  }
  return `Through ${trimmed}`;
};

/**
 * Derives the warehouse `year_mo` key from a persisted report period label.
 * Monthly reports use the end month; quarterly reports use the quarter end month
 * inside the parenthetical range. JOC (adhoc) periods have no single `year_mo`.
 * @param reportingPeriod - Stored `reports.reporting_period` label.
 * @param cadence - Report cadence.
 * @returns `YYYYMM` for monthly/quarterly, or null for adhoc / unparsable labels.
 */
export const reportingPeriodToYearMo = (
  reportingPeriod: string,
  cadence: Cadence,
): string | null => {
  const period = reportingPeriod.trim();
  if (!period || cadence === 'adhoc') {
    return null;
  }

  if (cadence === 'monthly') {
    if (period.includes('–')) {
      const end = period.split('–').pop()?.trim() ?? '';
      return parseReportMonthLabelToYearMo(end);
    }
    return parseReportMonthLabelToYearMo(period);
  }

  const rangeMatch = /\(([^)]+)\)\s*$/.exec(period);
  if (rangeMatch) {
    const end = rangeMatch[1].split('–').pop()?.trim() ?? '';
    return parseReportMonthLabelToYearMo(end);
  }

  return null;
};

const MONTH_NUM_TO_ABBREV: Record<string, string> = {
  '01': 'Jan',
  '02': 'Feb',
  '03': 'Mar',
  '04': 'Apr',
  '05': 'May',
  '06': 'Jun',
  '07': 'Jul',
  '08': 'Aug',
  '09': 'Sep',
  '10': 'Oct',
  '11': 'Nov',
  '12': 'Dec',
};

/** Default number of months in the member engagement trend chart. */
export const ENGAGEMENT_TREND_MONTH_COUNT = 6;

/**
 * Formats `YYYYMM` as a report month label (`Dec 2025`).
 * @param yearMo - Six-digit warehouse period key.
 * @returns Label or null when not parseable.
 */
export const formatYearMoToReportMonthLabel = (
  yearMo: string,
): string | null => {
  const normalized = yearMo.trim();
  if (!/^\d{6}$/.test(normalized)) {
    return null;
  }
  const year = normalized.slice(0, 4);
  const monthAbbrev = MONTH_NUM_TO_ABBREV[normalized.slice(4, 6)];
  if (!monthAbbrev) {
    return null;
  }
  return `${monthAbbrev} ${year}`;
};

/**
 * Builds consecutive `YYYYMM` keys ending at `endYearMo` (inclusive), oldest first.
 * @param endYearMo - Last month in the range.
 * @param monthCount - Number of months (e.g. 6).
 * @returns Ordered keys or null when inputs are invalid.
 */
export const buildYearMoRangeEndingAt = (
  endYearMo: string,
  monthCount: number,
): string[] | null => {
  const end = endYearMo.trim();
  if (!/^\d{6}$/.test(end) || monthCount < 1) {
    return null;
  }

  const months: string[] = [];
  let year = Number.parseInt(end.slice(0, 4), 10);
  let month = Number.parseInt(end.slice(4, 6), 10);

  for (let i = 0; i < monthCount; i += 1) {
    months.unshift(`${year}${String(month).padStart(2, '0')}`);
    month -= 1;
    if (month === 0) {
      month = 12;
      year -= 1;
    }
  }

  return months;
};

/**
 * Returns the last fully completed calendar month as `YYYYMM`.
 * The current month is excluded because warehouse monthly metrics are not closed yet.
 * @param asOf - Instant to evaluate (defaults to now, UTC).
 * @returns Previous calendar month, e.g. `202607` on 24 Aug 2026.
 */
export const lastCompletedCalendarYearMo = (asOf: Date = new Date()): string => {
  const year = asOf.getUTCFullYear();
  const monthIndex = asOf.getUTCMonth();
  if (monthIndex === 0) {
    return `${year - 1}12`;
  }
  return `${year}${String(monthIndex).padStart(2, '0')}`;
};

/**
 * Caps a warehouse `YYYYMM` so it is never a current or future month.
 * Past period-end months are left unchanged.
 * @param yearMo - Candidate warehouse month.
 * @param asOf - Instant to evaluate (defaults to now, UTC).
 * @returns `yearMo` or the last completed month, whichever is earlier.
 */
export const clampYearMoToLastCompleted = (
  yearMo: string,
  asOf: Date = new Date(),
): string => {
  const trimmed = yearMo.trim();
  if (!/^\d{6}$/.test(trimmed)) {
    return trimmed;
  }
  const lastCompleted = lastCompletedCalendarYearMo(asOf);
  return trimmed > lastCompleted ? lastCompleted : trimmed;
};

/** Default number of months in the VBP engagement growth chart when not specified. */
export const VBP_GROWTH_MONTH_COUNT = 6;

/**
 * Coerces snapshot `sixMonthGrowthYearMos` to six-digit `YYYYMM` keys.
 * @param raw - Stored year-month list from a VBP snapshot.
 * @returns Parsed keys; empty when the field is missing or malformed.
 */
export const coerceVbpGrowthYearMos = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map(value => String(value).trim().padStart(6, '0'))
    .filter(yearMo => /^\d{6}$/.test(yearMo));
};

/**
 * Builds chart points for VBP six-month growth from numeric values.
 * Prefer `yearMos` persisted with the series; otherwise derive months from `endYearMo`.
 * @param values - Engaged counts oldest to newest.
 * @param endYearMo - Last month in the series (`YYYYMM`) when `yearMos` is absent.
 * @param yearMos - Optional `YYYYMM` keys aligned to `values` (warehouse snapshot).
 * @returns Points with month labels such as `Mar 2027`.
 */
export const buildVbpGrowthChartPoints = (
  values: number[],
  endYearMo: string | null,
  yearMos?: readonly string[] | null,
): Array<{ month: string; engaged: number }> => {
  if (values.length === 0) {
    return [];
  }

  const persistedYearMos =
    yearMos && yearMos.length === values.length ? [...yearMos] : null;
  const derivedYearMos =
    persistedYearMos === null && endYearMo !== null
      ? buildYearMoRangeEndingAt(endYearMo, values.length)
      : persistedYearMos;

  return values.map((engaged, index) => {
    const yearMo = derivedYearMos?.[index];
    const month =
      yearMo !== undefined
        ? (formatYearMoToReportMonthLabel(yearMo) ?? yearMo)
        : `Month ${index + 1}`;
    return { month, engaged };
  });
};

/**
 * Resolves the end `YYYYMM` for labeling VBP growth charts from report context.
 * Period-end months that are in progress or in the future are replaced with
 * the last completed calendar month (same clamp as hero warehouse KPIs).
 * @param reportingPeriod - Stored report period label.
 * @param measurementPeriod - Optional warehouse measurement period from snapshot.
 * @param asOf - Instant to evaluate (defaults to now).
 * @returns End month key.
 */
export const resolveVbpGrowthChartEndYearMo = (
  reportingPeriod: string,
  measurementPeriod?: string | null,
  asOf: Date = new Date(),
): string => {
  const candidates = [measurementPeriod, reportingPeriod];
  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }
    const end = parseMeasurementPeriodEndYearMo(candidate);
    if (end) {
      return clampYearMoToLastCompleted(end, asOf);
    }
  }
  return lastCompletedCalendarYearMo(asOf);
};

/**
 * Maps a `YYYYMM` warehouse key to `YYYYQ#` (e.g. `202606` → `2026Q2`).
 * @param yearMo - Six-digit warehouse period key.
 * @returns Quarter key.
 */
export const yearMoToYearQtr = (yearMo: string): string => {
  const month = Number.parseInt(yearMo.slice(4, 6), 10);
  const year = yearMo.slice(0, 4);
  return `${year}Q${Math.ceil(month / 3)}`;
};

/**
 * Formats `YYYYQ#` as a report quarter label (`Q2'26`).
 * @param yearQtr - Warehouse quarter key.
 * @returns Label or null when not parseable.
 */
export const formatYearQtrToReportLabel = (yearQtr: string): string | null => {
  const match = /^(\d{4})Q([1-4])$/.exec(yearQtr.trim());
  if (!match) {
    return null;
  }
  return `Q${match[2]}'${match[1].slice(2)}`;
};

/**
 * Builds consecutive `YYYYQ#` keys ending at the quarter containing `endYearMo`.
 * @param endYearMo - Last month in the range (`YYYYMM`).
 * @param qtrCount - Number of quarters (e.g. 4).
 * @returns Ordered quarter keys or null when inputs are invalid.
 */
export const buildYearQtrRangeEndingAt = (
  endYearMo: string,
  qtrCount: number,
): string[] | null => {
  const end = yearMoToYearQtr(endYearMo.trim());
  const match = /^(\d{4})Q([1-4])$/.exec(end);
  if (!match || qtrCount < 1) {
    return null;
  }

  let year = Number.parseInt(match[1], 10);
  let qtr = Number.parseInt(match[2], 10);
  const quarters: string[] = [];

  for (let i = 0; i < qtrCount; i += 1) {
    quarters.unshift(`${year}Q${qtr}`);
    qtr -= 1;
    if (qtr === 0) {
      qtr = 4;
      year -= 1;
    }
  }

  return quarters;
};
